import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Users, Calendar, Package, MessageSquare, Edit3, Loader2, Check, ChevronDown, ChevronUp, Plus, Trash2, Clock, Ban, Upload, X, Mail, BarChart2, Send, ArrowUp, ArrowDown, Eye } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ["pending_payment", "confirmed", "completed", "cancelled"];

// ─── Reusable field components ──────────────────────────────────────────────────

function Field({ label, value, onChange, multiline = false, rows = 2 }) {
  return (
    <div>
      <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={rows}
          className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors resize-none"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
        />
      )}
    </div>
  );
}

function Section({ title, open, onToggle, children }) {
  return (
    <div className="border border-soft-gray bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[#F9F9F6] transition-colors"
      >
        <span className="font-lora text-navy text-base">{title}</span>
        {open ? <ChevronUp size={16} className="text-navy/40" /> : <ChevronDown size={16} className="text-navy/40" />}
      </button>
      {open && <div className="px-6 pb-6 pt-2 space-y-4 border-t border-soft-gray">{children}</div>}
    </div>
  );
}

// ─── Blog CMS ────────────────────────────────────────────────────────────────────

const EMPTY_ARTICLE = { title: "", subtitle: "", date: "", publishDate: "", readTime: "", image: "", slug: "", body: "", status: "draft" };

function titleToSlug(t) {
  return t.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();
}

// Normalize body from DB: legacy articles store body as string[], new ones as markdown string
function bodyToText(body) {
  return Array.isArray(body) ? body.join("\n\n") : (body || "");
}

function BlogCMS() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, object = form
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [reordering, setReordering] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/articles`, { withCredentials: true })
      .then(res => { setArticles(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  // Intentional: empty dep array runs this only on mount; `load` is a stable local fn and re-including it would cause re-fetch loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);

  function openNew() {
    setEditing({ ...EMPTY_ARTICLE });
    setIsNew(true);
  }

  function openEdit(a) {
    setEditing({ ...a, body: bodyToText(a.body) });
    setIsNew(false);
  }

  function closeForm() { setEditing(null); setIsNew(false); }

  function setField(key, val) {
    setEditing(prev => {
      const next = { ...prev, [key]: val };
      if (key === "title" && isNew) next.slug = titleToSlug(val);
      return next;
    });
  }

  async function save() {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (isNew) {
        await axios.post(`${API}/admin/articles`, payload, { withCredentials: true });
        toast.success("Article created.");
        closeForm();
        load();
      } else {
        const res = await axios.put(`${API}/admin/articles/${editing.slug}`, payload, { withCredentials: true });
        setArticles(prev => prev.map(a => a.slug === editing.slug ? res.data : a));
        toast.success("Article updated.");
        closeForm();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save. Please try again.");
    }
    setSaving(false);
  }

  async function confirmDelete(slug) {
    setDeleting(slug);
    try {
      await axios.delete(`${API}/admin/articles/${slug}`, { withCredentials: true });
      setArticles(prev => prev.filter(a => a.slug !== slug));
      toast.success("Article deleted.");
    } catch {
      toast.error("Failed to delete article.");
    }
    setDeleting(null);
  }

  async function moveArticle(slug, direction) {
    const idx = articles.findIndex(a => a.slug === slug);
    if (idx === -1) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= articles.length) return;
    const newArticles = [...articles];
    [newArticles[idx], newArticles[swapIdx]] = [newArticles[swapIdx], newArticles[idx]];
    setArticles(newArticles);
    setReordering(true);
    try {
      const payload = newArticles.map((a, i) => ({ slug: a.slug, order: i }));
      await axios.put(`${API}/admin/articles/reorder`, payload, { withCredentials: true });
    } catch {
      toast.error("Failed to reorder articles.");
      load();
    }
    setReordering(false);
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setField("image", `${process.env.REACT_APP_BACKEND_URL}${res.data.path}`);
      toast.success("Image uploaded.");
    } catch {
      toast.error("Image upload failed. Try pasting a URL instead.");
    }
    setUploadingImage(false);
    e.target.value = "";
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-navy/30" /></div>;

  if (editing) {
    return (
      <div data-testid="blog-article-form">
        {/* Form Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-lora text-navy text-lg font-normal">{isNew ? "New Article" : "Edit Article"}</h3>
            <p className="text-navy/40 font-serif text-xs mt-0.5">Changes will appear on the Insights page based on the publish date.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={closeForm} className="font-serif text-sm text-navy/40 hover:text-navy underline underline-offset-2 transition-colors" data-testid="cancel-article-form">Cancel</button>
            <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-navy text-ivory px-6 py-2.5 text-xs tracking-widest uppercase hover:bg-navy/90 transition-all disabled:opacity-60" data-testid="save-article-btn">
              {saving ? <><Loader2 size={13} className="animate-spin" /> Saving...</> : isNew ? "Publish Article" : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="bg-white border border-soft-gray p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">Title *</label>
            <input data-testid="article-title-input" value={editing.title} onChange={e => setField("title", e.target.value)}
              className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-lora text-base focus:outline-none focus:border-navy transition-colors" placeholder="Article title…" />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">Subtitle</label>
            <input data-testid="article-subtitle-input" value={editing.subtitle} onChange={e => setField("subtitle", e.target.value)}
              className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors" placeholder="A short descriptive line…" />
          </div>

          {/* Row: display date, publish date, read time */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">Display Date</label>
              <input data-testid="article-date-input" value={editing.date} onChange={e => setField("date", e.target.value)}
                className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors" placeholder="e.g. April 2026" />
            </div>
            <div>
              <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">Publish Date</label>
              <input type="date" data-testid="article-publish-input" value={editing.publishDate} onChange={e => setField("publishDate", e.target.value)}
                className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors" />
            </div>
            <div>
              <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">Read Time</label>
              <input data-testid="article-readtime-input" value={editing.readTime} onChange={e => setField("readTime", e.target.value)}
                className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors" placeholder="e.g. 4 min read" />
            </div>
          </div>

          {/* Hero Image — upload or paste URL */}
          <div>
            <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-2">Hero Image</label>

            {/* Image preview */}
            {editing.image && (
              <div className="relative mb-3 group" data-testid="article-image-preview-container">
                <img
                  src={editing.image}
                  alt="Hero preview"
                  className="w-full h-44 object-cover"
                  onError={e => { e.target.style.display = "none"; }}
                  data-testid="article-image-preview"
                />
                <button
                  type="button"
                  onClick={() => setField("image", "")}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-navy/50 hover:text-red-500 p-1.5 opacity-0 group-hover:opacity-100 transition-all"
                  aria-label="Remove image"
                  data-testid="article-image-remove"
                >
                  <X size={13} />
                </button>
              </div>
            )}

            {/* Upload button */}
            <div className="flex items-center gap-3 mb-2">
              <label
                className={`flex items-center gap-2 border border-soft-gray bg-white px-4 py-2 text-xs tracking-widest uppercase cursor-pointer hover:bg-[#F9F9F6] transition-colors select-none ${uploadingImage ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="article-image-upload-label"
              >
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="article-image-upload"
                  disabled={uploadingImage}
                />
                {uploadingImage
                  ? <><Loader2 size={12} className="animate-spin" /> Uploading…</>
                  : <><Upload size={12} /> Upload Image</>}
              </label>
              <span className="text-navy/30 text-xs font-serif">or paste a URL below</span>
            </div>

            <input
              data-testid="article-image-input"
              value={editing.image}
              onChange={e => setField("image", e.target.value)}
              className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
              placeholder="https://…"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">
              URL Slug {isNew && <span className="text-navy/30">(auto-generated from title)</span>}
            </label>
            <input data-testid="article-slug-input" value={editing.slug} onChange={e => setField("slug", e.target.value)} readOnly={!isNew}
              className={`w-full border border-soft-gray px-3 py-2 text-navy font-mono text-sm focus:outline-none focus:border-navy transition-colors ${!isNew ? "bg-[#F9F9F6] text-navy/50" : "bg-white"}`} placeholder="article-url-slug" />
          </div>

          {/* Body — WYSIWYG Rich Text Editor */}
          <div>
            <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">
              Body
            </label>
            <div data-testid="article-body-input">
              <RichTextEditor
                value={editing.body}
                onChange={val => setField("body", val)}
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">
              Status
            </label>
            <div className="flex gap-2">
              {["draft", "published"].map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setField("status", s)}
                  className={`px-5 py-2 text-xs tracking-widest uppercase font-serif border transition-all ${
                    editing.status === s
                      ? "bg-navy text-ivory border-navy"
                      : "bg-white text-navy/50 border-soft-gray hover:border-navy/40"
                  }`}
                  data-testid={`status-btn-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
            {editing.status === "published" && !editing.publishDate && (
              <p className="text-amber-600 text-xs mt-1 font-serif">Set a publish date to control when the article goes live.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-cms">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-lora text-navy text-lg font-normal">Insights Articles</h3>
          <p className="text-navy/40 font-serif text-xs mt-0.5">{articles.length} article{articles.length !== 1 ? "s" : ""} total. Future-dated articles are published automatically on their date.</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-navy text-ivory px-6 py-2.5 text-xs tracking-widest uppercase hover:bg-navy/90 transition-all" data-testid="new-article-btn">
          <Plus size={13} /> New Article
        </button>
      </div>

      <div className="space-y-2">
        {articles.length === 0 && <div className="bg-white border border-soft-gray text-center py-16"><p className="text-navy/30 font-serif text-sm">No articles yet. Click "New Article" to get started.</p></div>}
        {articles.map(a => {
          const published = a.publishDate <= today;
          return (
            <div key={a.slug} className="bg-white border border-soft-gray p-5 flex items-center gap-5" data-testid={`article-row-${a.slug}`}>
              {a.image && <img src={a.image} alt="" className="w-16 h-12 object-cover shrink-0 opacity-80" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  {a.status === "draft"
                    ? <span className="text-[10px] px-2 py-0.5 tracking-widest uppercase bg-gray-50 text-gray-500 border border-gray-200">Draft</span>
                    : a.publishDate && a.publishDate > today
                      ? <span className="text-[10px] px-2 py-0.5 tracking-widest uppercase bg-blue-50 text-blue-700 border border-blue-200">Scheduled</span>
                      : <span className="text-[10px] px-2 py-0.5 tracking-widest uppercase bg-green-50 text-green-700 border border-green-200">Published</span>
                  }
                  <span className="text-navy/30 text-xs">{a.date} · {a.readTime}</span>
                </div>
                <p className="font-lora text-navy text-sm leading-snug truncate">{a.title}</p>
                {a.subtitle && <p className="text-navy/40 font-serif text-xs truncate mt-0.5">{a.subtitle}</p>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {/* Reorder arrows */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveArticle(a.slug, "up")}
                    disabled={reordering || articles.indexOf(a) === 0}
                    className="text-navy/30 hover:text-navy transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                    data-testid={`move-up-${a.slug}`}
                    aria-label="Move up"
                    title="Move up"
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    onClick={() => moveArticle(a.slug, "down")}
                    disabled={reordering || articles.indexOf(a) === articles.length - 1}
                    className="text-navy/30 hover:text-navy transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                    data-testid={`move-down-${a.slug}`}
                    aria-label="Move down"
                    title="Move down"
                  >
                    <ArrowDown size={13} />
                  </button>
                </div>
                <button onClick={() => openEdit(a)} className="text-navy/40 hover:text-navy text-xs tracking-widest uppercase transition-colors" data-testid={`edit-article-${a.slug}`}>Edit</button>
                <button
                  onClick={() => confirmDelete(a.slug)}
                  disabled={deleting === a.slug}
                  className="text-red-300 hover:text-red-600 transition-colors disabled:opacity-40"
                  data-testid={`delete-article-${a.slug}`}
                  aria-label="Delete article"
                >
                  {deleting === a.slug ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Availability Editor ─────────────────────────────────────────────────────────

const ALL_SLOTS = [
  "7:00 AM","7:15 AM","7:30 AM","7:45 AM",
  "8:00 AM","8:15 AM","8:30 AM","8:45 AM",
  "9:00 AM","9:15 AM","9:30 AM","9:45 AM",
  "10:00 AM","10:15 AM","10:30 AM","10:45 AM",
  "11:00 AM","11:15 AM","11:30 AM","11:45 AM",
  "12:00 PM","12:15 PM","12:30 PM","12:45 PM",
  "1:00 PM","1:15 PM","1:30 PM","1:45 PM",
  "2:00 PM","2:15 PM","2:30 PM","2:45 PM",
  "3:00 PM","3:15 PM","3:30 PM","3:45 PM",
  "4:00 PM","4:15 PM","4:30 PM","4:45 PM",
  "5:00 PM","5:15 PM","5:30 PM","5:45 PM",
  "6:00 PM","6:15 PM","6:30 PM","6:45 PM",
  "7:00 PM","7:15 PM","7:30 PM","7:45 PM",
  "8:00 PM","8:15 PM","8:30 PM","8:45 PM",
  "9:00 PM","9:15 PM","9:30 PM","9:45 PM",
  "10:00 PM",
];

function timeToMinutes(slot) {
  const m = slot.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 9999;
  let h = parseInt(m[1]), min = parseInt(m[2]), ampm = m[3].toUpperCase();
  if (ampm === "PM" && h !== 12) h += 12;
  else if (ampm === "AM" && h === 12) h = 0;
  return h * 60 + min;
}

function SlotSection({ label, slots, setSlots, testPrefix }) {
  const [addSlot, setAddSlot] = useState("");
  const available = ALL_SLOTS.filter(s => !slots.includes(s));

  function add() {
    if (!addSlot || slots.includes(addSlot)) return;
    setSlots(prev => [...prev, addSlot].sort((a, b) => timeToMinutes(a) - timeToMinutes(b)));
    setAddSlot("");
  }

  return (
    <div className="border border-soft-gray bg-white">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-soft-gray">
        <Clock size={14} className="text-navy/40" />
        <span className="font-lora text-navy text-sm">{label}</span>
        <span className="ml-auto text-navy/30 text-xs">{slots.length} slot{slots.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-slots-list`}>
          {slots.length === 0 && <p className="text-navy/30 font-serif text-sm">No slots configured.</p>}
          {slots.map(slot => (
            <div key={slot} className="flex items-center gap-2 bg-[#F9F9F6] border border-soft-gray px-3 py-1.5" data-testid={`${testPrefix}-chip-${slot.replace(/[\s:]/g, "-")}`}>
              <span className="font-serif text-navy text-sm">{slot}</span>
              <button onClick={() => setSlots(prev => prev.filter(s => s !== slot))} className="text-navy/30 hover:text-red-500 transition-colors" aria-label={`Remove ${slot}`}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 pt-2 border-t border-soft-gray">
          <select value={addSlot} onChange={e => setAddSlot(e.target.value)}
            className="border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
            data-testid={`${testPrefix}-add-select`}>
            <option value="">Add a time slot…</option>
            {available.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={add} disabled={!addSlot}
            className="flex items-center gap-1.5 bg-navy text-ivory px-4 py-2 text-xs tracking-widest uppercase hover:bg-navy/90 transition-all disabled:opacity-40"
            data-testid={`${testPrefix}-add-btn`}>
            <Plus size={12} /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

function AvailabilityEditor() {
  const [mondaySlots, setMondaySlots] = useState([]);
  const [weekdaySlots, setWeekdaySlots] = useState([]);
  const [fridaySlots, setFridaySlots] = useState([]);
  const [saturdaySlots, setSaturdaySlots] = useState([]);
  const [sundaySlots, setSundaySlots] = useState([]);
  const [blackout, setBlackout] = useState([]);
  const [addDate, setAddDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/admin/availability`, { withCredentials: true })
      .then(res => {
        setMondaySlots(res.data.monday_slots || []);
        setWeekdaySlots(res.data.weekday_slots || []);
        setFridaySlots(res.data.friday_slots || []);
        setSaturdaySlots(res.data.saturday_slots || []);
        setSundaySlots(res.data.sunday_slots || []);
        setBlackout(res.data.blackout_dates || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/availability`, {
        monday_slots: mondaySlots,
        weekday_slots: weekdaySlots,
        friday_slots: fridaySlots,
        saturday_slots: saturdaySlots,
        sunday_slots: sundaySlots,
        blackout_dates: blackout,
      }, { withCredentials: true });
      toast.success("Availability settings saved.");
    } catch {
      toast.error("Failed to save. Please try again.");
    }
    setSaving(false);
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-navy/30" /></div>;

  return (
    <div data-testid="availability-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-lora text-navy text-lg font-normal">Availability Settings</h3>
          <p className="text-navy/40 font-serif text-xs mt-0.5">Set time slots per day type. Clients see only slots for the day they select. All times are Eastern Time.</p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-navy text-ivory px-6 py-2.5 text-xs tracking-widest uppercase hover:bg-navy/90 transition-all disabled:opacity-60"
          data-testid="save-availability-btn">
          {saving ? <><Loader2 size={13} className="animate-spin" /> Saving...</> : "Save Changes"}
        </button>
      </div>

      <div className="space-y-4">
        <SlotSection label="Monday" slots={mondaySlots} setSlots={setMondaySlots} testPrefix="monday" />
        <SlotSection label="Tuesday – Thursday" slots={weekdaySlots} setSlots={setWeekdaySlots} testPrefix="weekday" />
        <SlotSection label="Friday" slots={fridaySlots} setSlots={setFridaySlots} testPrefix="friday" />
        <SlotSection label="Saturday" slots={saturdaySlots} setSlots={setSaturdaySlots} testPrefix="saturday" />
        <SlotSection label="Sunday" slots={sundaySlots} setSlots={setSundaySlots} testPrefix="sunday" />

        {/* Blackout Dates */}
        <div className="border border-soft-gray bg-white">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-soft-gray">
            <Ban size={14} className="text-navy/40" />
            <span className="font-lora text-navy text-sm">Blackout Dates</span>
            <span className="ml-auto text-navy/30 text-xs">{blackout.length} blocked</span>
          </div>
          <div className="px-6 py-5 space-y-4">
            <p className="text-navy/40 font-serif text-xs">Clients cannot book on these dates (holidays, personal days, etc.).</p>
            <div className="flex flex-wrap gap-2" data-testid="blackout-list">
              {blackout.length === 0 && <p className="text-navy/30 font-serif text-sm">No dates blocked.</p>}
              {blackout.map(d => (
                <div key={d} className="flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-1.5" data-testid={`blackout-chip-${d}`}>
                  <span className="font-serif text-red-700 text-sm">{d}</span>
                  <button onClick={() => setBlackout(prev => prev.filter(x => x !== d))} className="text-red-300 hover:text-red-600 transition-colors" aria-label={`Unblock ${d}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-soft-gray">
              <input type="date" value={addDate} onChange={e => setAddDate(e.target.value)}
                className="border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                data-testid="add-blackout-date" />
              <button onClick={() => { if (!addDate || blackout.includes(addDate)) return; setBlackout(prev => [...prev, addDate].sort()); setAddDate(""); }}
                disabled={!addDate}
                className="flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 text-xs tracking-widest uppercase transition-all disabled:opacity-40"
                data-testid="add-blackout-btn">
                <Ban size={12} /> Block Date
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Content Editor ──────────────────────────────────────────────────────────────

function ContentEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openSections, setOpenSections] = useState({ hero: true, testimonials: false, services: false, email: false });

  // Intentional: empty dep array loads CMS content once on mount; re-running on every render would overwrite unsaved edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/admin/content`, { withCredentials: true })
      .then(res => { setContent(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = key => setOpenSections(p => ({ ...p, [key]: !p[key] }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.warn("[Admin] Save failed:", err?.message || err); }
    setSaving(false);
  };

  const setHero = (key, val) => setContent(p => ({ ...p, hero: { ...p.hero, [key]: val } }));
  const setTestimonial = (i, key, val) => setContent(p => {
    const ts = [...(p.testimonials || [])];
    ts[i] = { ...ts[i], [key]: val };
    return { ...p, testimonials: ts };
  });
  const setService = (i, key, val) => setContent(p => {
    const svcs = [...(p.services || [])];
    svcs[i] = { ...svcs[i], [key]: val };
    return { ...p, services: svcs };
  });
  const setServiceInclude = (si, ii, val) => setContent(p => {
    const svcs = [...(p.services || [])];
    const includes = [...(svcs[si].includes || [])];
    includes[ii] = val;
    svcs[si] = { ...svcs[si], includes };
    return { ...p, services: svcs };
  });
  const addInclude = (si) => setContent(p => {
    const svcs = [...(p.services || [])];
    svcs[si] = { ...svcs[si], includes: [...(svcs[si].includes || []), ""] };
    return { ...p, services: svcs };
  });
  const removeInclude = (si, ii) => setContent(p => {
    const svcs = [...(p.services || [])];
    const includes = svcs[si].includes.filter((_, idx) => idx !== ii);
    svcs[si] = { ...svcs[si], includes };
    return { ...p, services: svcs };
  });
  const setEmail = (key, val) => setContent(p => ({ ...p, email: { ...p.email, [key]: val } }));

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-navy/30" /></div>;
  if (!content) return <p className="text-navy/40 font-serif text-center py-10">Failed to load content.</p>;

  return (
    <div data-testid="content-editor">
      {/* Save Bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-lora text-navy text-lg font-normal">Site Content Editor</h3>
          <p className="text-navy/40 font-serif text-xs mt-0.5">Changes are reflected live on the website after saving.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-navy text-ivory px-6 py-2.5 text-xs tracking-widest uppercase hover:bg-navy/90 transition-all disabled:opacity-60"
          data-testid="save-content-btn"
        >
          {saving ? <><Loader2 size={13} className="animate-spin" /> Saving...</> : saved ? <><Check size={13} /> Saved</> : "Save Changes"}
        </button>
      </div>

      <div className="space-y-2">
        {/* Hero Section */}
        <Section title="Hero Section" open={openSections.hero} onToggle={() => toggle("hero")}>
          <p className="text-navy/40 font-serif text-xs mb-1">Edits the homepage hero headline, eyebrow, and body paragraph.</p>
          <Field label="Eyebrow (above headline)" value={content.hero?.subheadline || ""} onChange={v => setHero("subheadline", v)} />
          <Field label="Headline" value={content.hero?.headline || ""} onChange={v => setHero("headline", v)} multiline rows={2} />
          <Field label="Body Text" value={content.hero?.body || ""} onChange={v => setHero("body", v)} multiline rows={4} />
        </Section>

        {/* Testimonials */}
        <Section title="Testimonials" open={openSections.testimonials} onToggle={() => toggle("testimonials")}>
          {(content.testimonials || []).map((t, i) => (
            <div key={`testimonial-${i}`} className="border border-soft-gray p-4 space-y-3">
              <p className="font-lora text-xs tracking-widest uppercase text-navy/30">Testimonial {i + 1}</p>
              <Field label="Quote" value={t.quote || ""} onChange={v => setTestimonial(i, "quote", v)} multiline rows={3} />
              <Field label="Attribution" value={t.attribution || ""} onChange={v => setTestimonial(i, "attribution", v)} />
            </div>
          ))}
        </Section>

        {/* Services */}
        <Section title="Services & Pricing" open={openSections.services} onToggle={() => toggle("services")}>
          {(content.services || []).map((svc, i) => (
            <div key={svc.id} className="border border-soft-gray p-4 space-y-3">
              <p className="font-lora text-xs tracking-widest uppercase text-navy/30">{svc.name}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Service Name" value={svc.name || ""} onChange={v => setService(i, "name", v)} />
                <div>
                  <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">Price (CAD)</label>
                  <input
                    type="number"
                    value={svc.price || ""}
                    onChange={e => setService(i, "price", parseFloat(e.target.value) || 0)}
                    className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                  />
                </div>
              </div>
              <Field label="Description" value={svc.description || ""} onChange={v => setService(i, "description", v)} multiline rows={2} />
              <Field label="Outcome" value={svc.outcome || ""} onChange={v => setService(i, "outcome", v)} multiline rows={2} />
              {svc.includes?.length > 0 && (
                <div>
                  <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-2">What's Included</label>
                  <div className="space-y-2">
                    {svc.includes.map((inc, ii) => (
                      <div key={`include-${ii}`} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={inc}
                          onChange={e => setServiceInclude(i, ii, e.target.value)}
                          className="flex-1 border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                        />
                        <button onClick={() => removeInclude(i, ii)} className="text-red-400 hover:text-red-600 transition-colors p-1">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => addInclude(i)} className="mt-2 flex items-center gap-1 text-navy/40 hover:text-navy text-xs tracking-wide transition-colors">
                    <Plus size={12} /> Add item
                  </button>
                </div>
              )}
            </div>
          ))}
        </Section>

        {/* Email Template */}
        <Section title="Email Template" open={openSections.email} onToggle={() => toggle("email")}>
          <p className="text-navy/40 font-serif text-xs">These fields update the booking confirmation email sent to clients.</p>
          <Field label="Email Subject" value={content.email?.subject || ""} onChange={v => setEmail("subject", v)} />
          <Field label="Opening Line" value={content.email?.intro || ""} onChange={v => setEmail("intro", v)} multiline rows={3} />
          <Field label="Closing Line" value={content.email?.closing || ""} onChange={v => setEmail("closing", v)} multiline rows={2} />
        </Section>
      </div>
    </div>
  );
}

// ─── Analytics Dashboard ─────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent = false }) {
  return (
    <div className={`bg-white border ${accent ? "border-gold" : "border-soft-gray"} p-6`} data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className={`font-lora text-3xl ${accent ? "text-navy" : "text-navy"}`}>{value}</p>
      <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mt-1">{label}</p>
      {sub && <p className="font-serif text-xs text-navy/30 mt-1">{sub}</p>}
    </div>
  );
}

function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Intentional: empty dep array fetches analytics once on mount; API is stable and re-fetching on every render is unnecessary.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/admin/analytics`, { withCredentials: true })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-navy/30" /></div>;
  if (!data) return <p className="text-navy/40 font-serif text-center py-10">Unable to load analytics.</p>;

  const total = data.total_downloads;

  return (
    <div data-testid="analytics-dashboard">
      <div className="mb-6">
        <h3 className="font-lora text-navy text-lg font-normal">Lead Magnet Analytics</h3>
        <p className="text-navy/40 font-serif text-xs mt-0.5">Career Roadmap funnel — downloads, opens, CTAs, nurture sequence, unsubscribes.</p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total Downloads" value={total} accent />
        <StatCard label="Emails Opened" value={data.emails_opened} sub={`${data.open_rate_pct}% open rate`} />
        <StatCard label="CTA Clicks" value={data.cta_clicked} sub={`${data.cta_click_rate_pct}% click rate`} />
        <StatCard label="Bookings from Emails" value={data.bookings_from_roadmap} />
        <StatCard label="Unsubscribed" value={data.unsubscribed} sub={`${data.unsubscribe_rate_pct}% rate`} />
      </div>

      {/* Source breakdown — outside the stat grid */}
      {Object.keys(data.booking_source_breakdown || {}).length > 0 && (
        <div className="bg-white border border-soft-gray p-5 mb-8" data-testid="booking-source-breakdown">
          <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mb-4">Booking Source Attribution</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.booking_source_breakdown).map(([src, count]) => (
              <div key={src} className="flex items-center gap-2 border border-soft-gray px-3 py-2">
                <span className="font-lora text-sm text-navy">{count}</span>
                <span className="font-serif text-xs text-navy/50">{src.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Funnel */}
      <div className="bg-white border border-soft-gray p-6 mb-6">
        <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mb-5">Conversion Funnel</p>
        {[
          { label: "Downloaded Roadmap", count: total, pct: 100 },
          { label: "Opened Email", count: data.emails_opened, pct: total ? Math.round(data.emails_opened / total * 100) : 0 },
          { label: "Clicked CTA", count: data.cta_clicked, pct: total ? Math.round(data.cta_clicked / total * 100) : 0 },
          { label: "Booked Advisory Call", count: data.bookings_from_roadmap, pct: total ? Math.round(data.bookings_from_roadmap / total * 100) : 0 },
        ].map(row => (
          <div key={row.label} className="mb-4" data-testid={`funnel-row-${row.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex justify-between mb-1">
              <span className="font-serif text-sm text-navy/70">{row.label}</span>
              <span className="font-lora text-sm text-navy">{row.count} <span className="text-navy/30 font-serif text-xs">({row.pct}%)</span></span>
            </div>
            <div className="h-1.5 bg-soft-gray rounded-none overflow-hidden">
              <div className="h-full bg-navy/30 transition-all duration-700" style={{ width: `${row.pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Nurture sequence */}
      <div className="bg-white border border-soft-gray p-6 mb-6">
        <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mb-5">Nurture Sequence Sent</p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { key: "email_2", label: "Day 7 — Reflection", desc: "You Do Not Need Every Answer Today" },
            { key: "email_3", label: "Day 21 — Obstacles", desc: "Where Are You Stuck Right Now?" },
            { key: "email_4", label: "Day 45 — Advisory", desc: "A Strategic Career Conversation" },
          ].map(e => (
            <div key={e.key} className="border border-soft-gray p-4" data-testid={`nurture-card-${e.key}`}>
              <p className="font-lora text-xl text-navy">{data.nurture_sent?.[e.key] ?? 0}</p>
              <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mt-1">{e.label}</p>
              <p className="font-serif text-xs text-navy/30 mt-1 italic">{e.desc}</p>
            </div>
          ))}
        </div>
        {total === 0 && (
          <p className="text-center text-navy/30 font-serif text-sm mt-4">No leads yet. Download data will appear here once the lead magnet form is used.</p>
        )}
      </div>

      {/* Re-engagement trigger */}
      <div className="bg-white border border-soft-gray p-6" data-testid="reengagement-section">
        <div className="flex items-center gap-3 mb-5">
          <p className="font-lora text-xs tracking-widest uppercase text-navy/40">Re-Engagement Trigger</p>
          <span className="text-[10px] px-2 py-0.5 border border-gold/60 text-gold/80 tracking-wider uppercase">Auto</span>
        </div>
        <p className="font-serif text-xs text-navy/40 mb-5">
          Sends "Still Thinking About Your Next Step?" exactly 7 days after a lead opens Email 3 (obstacles) — only if they have not yet booked a call.
        </p>
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-soft-gray p-4" data-testid="reengagement-eligible">
            <p className="font-lora text-xl text-navy">{data.re_engagement?.eligible ?? 0}</p>
            <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mt-1">Opened Email 3</p>
            <p className="font-serif text-xs text-navy/30 mt-1 italic">Eligible for trigger</p>
          </div>
          <div className="border border-soft-gray p-4" data-testid="reengagement-sent">
            <p className="font-lora text-xl text-navy">{data.re_engagement?.sent ?? 0}</p>
            <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mt-1">Re-Engaged</p>
            <p className="font-serif text-xs text-navy/30 mt-1 italic">Email sent</p>
          </div>
          <div className="border border-soft-gray p-4" data-testid="reengagement-skipped">
            <p className="font-lora text-xl text-navy">{data.re_engagement?.skipped_booked ?? 0}</p>
            <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mt-1">Skipped</p>
            <p className="font-serif text-xs text-navy/30 mt-1 italic">Already booked</p>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Open Rate Chart ─────────────────────────────────────────────────────────────

function OpenRateTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-navy text-ivory px-4 py-3 shadow-xl max-w-[220px]">
      <p className="font-lora text-[10px] tracking-widest uppercase text-gold/80 mb-1">{d.rate}% open rate</p>
      <p className="font-serif text-xs text-ivory/80 leading-snug mb-1 truncate">{d.subject}</p>
      <p className="font-serif text-[10px] text-ivory/40">{d.opens} of {d.recipients} opened &middot; {d.date}</p>
    </div>
  );
}

function OpenRateChart({ newsletters }) {
  const data = newsletters.slice(-12).map(nl => ({
    id: nl.id,
    subject: nl.subject || "",
    date: nl.sent_at?.substring(5, 10) || "",
    rate: nl.recipient_count ? Math.round((nl.open_count || 0) / nl.recipient_count * 100) : 0,
    opens: nl.open_count || 0,
    recipients: nl.recipient_count || 0,
  }));

  const avg = data.length ? Math.round(data.reduce((s, d) => s + d.rate, 0) / data.length) : 0;
  const best = Math.max(...data.map(d => d.rate), 0);

  const barColor = (rate) => rate >= 30 ? "#C2B8A3" : rate >= 20 ? "#001F3F" : "#CBD5E1";

  return (
    <div className="bg-white border border-soft-gray p-6 mb-6" data-testid="open-rate-chart">
      {/* Header row */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="font-lora text-xs tracking-widest uppercase text-navy/40">Open Rate by Issue</p>
          <p className="font-serif text-xs text-navy/25 mt-0.5">{data.length} newsletter{data.length !== 1 ? "s" : ""} sent · hover for details</p>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <p className="font-lora text-2xl text-navy font-normal" data-testid="chart-avg-rate">{avg}<span className="text-base text-navy/40">%</span></p>
            <p className="font-lora text-[10px] tracking-widest uppercase text-navy/30">Avg open rate</p>
          </div>
          <div>
            <p className="font-lora text-2xl font-normal" style={{ color: "#C2B8A3" }}>{best}<span className="text-base" style={{ color: "#C2B8A3", opacity: 0.6 }}>%</span></p>
            <p className="font-lora text-[10px] tracking-widest uppercase text-navy/30">Best issue</p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} barCategoryGap="30%" margin={{ top: 6, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E0DDD8" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontFamily: "Georgia, serif", fontSize: 10, fill: "#9B9B8A" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[0, Math.max(best + 10, 50)]}
            tickFormatter={v => `${v}%`}
            tick={{ fontFamily: "Georgia, serif", fontSize: 9, fill: "#9B9B8A" }}
            axisLine={false}
            tickLine={false}
            width={36}
          />
          <Tooltip content={<OpenRateTooltip />} cursor={{ fill: "#F9F9F6" }} />
          {avg > 0 && (
            <ReferenceLine
              y={avg}
              stroke="#C2B8A3"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              label={{ value: `avg ${avg}%`, position: "insideTopRight", fontFamily: "Georgia,serif", fontSize: 9, fill: "#C2B8A3" }}
            />
          )}
          <Bar dataKey="rate" radius={[2, 2, 0, 0]} maxBarSize={48}>
            {data.map((d, i) => (
              <Cell key={i} fill={barColor(d.rate)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex items-center gap-5 mt-3 pt-3 border-t border-soft-gray">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: "#C2B8A3" }} />
          <span className="font-serif text-[10px] text-navy/40">≥ 30% (strong)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-navy/60" />
          <span className="font-serif text-[10px] text-navy/40">20–29%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: "#CBD5E1" }} />
          <span className="font-serif text-[10px] text-navy/40">&lt; 20%</span>
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <div className="w-5 border-t-2 border-dashed" style={{ borderColor: "#C2B8A3" }} />
          <span className="font-serif text-[10px] text-navy/40">average</span>
        </div>
      </div>
    </div>
  );
}


// ─── Newsletter Tab ───────────────────────────────────────────────────────────────

function NewsletterTab() {
  const [audiences, setAudiences] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Composer state
  const [subject, setSubject] = useState("");
  const [contentMd, setContentMd] = useState("");
  const [audienceSlug, setAudienceSlug] = useState("career-roadmap-subscribers");
  const [draftId, setDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [showNewAudience, setShowNewAudience] = useState(false);
  const [newAud, setNewAud] = useState({ name: "", slug: "", description: "" });
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const activeAudience = audiences.find(a => a.slug === audienceSlug);

  // Intentional: empty dep array runs fetchAll once on mount; `fetchAll` is defined in the same scope and including it would cause an infinite loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [audRes, nlRes] = await Promise.all([
        axios.get(`${API}/admin/audiences`, { withCredentials: true }),
        axios.get(`${API}/admin/newsletters`, { withCredentials: true }),
      ]);
      setAudiences(audRes.data);
      setNewsletters(nlRes.data);
    } catch (err) { console.error('[Admin] Failed to load newsletter data:', err); }
    setLoading(false);
  }

  function loadDraft(nl) {
    setDraftId(nl.id);
    setSubject(nl.subject);
    setContentMd(nl.content_md);
    setAudienceSlug(nl.audience_slug || "career-roadmap-subscribers");
    setEditingDraft(nl);
    setLastResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetComposer() {
    setSubject(""); setContentMd(""); setDraftId(null); setEditingDraft(null); setLastResult(null);
    setAudienceSlug("career-roadmap-subscribers");
  }

  async function saveDraft() {
    if (!subject.trim() || !contentMd.trim()) { toast.error("Subject and content are required."); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/newsletters`, { subject, content_md: contentMd, audience_slug: audienceSlug, id: draftId }, { withCredentials: true });
      if (!draftId) setDraftId(res.data.id);
      toast.success("Draft saved.");
      fetchAll();
    } catch { toast.error("Failed to save draft."); }
    setSaving(false);
  }

  async function sendPreview() {
    if (!draftId) { await saveDraft(); }
    setPreviewing(true);
    try {
      const id = draftId || (await axios.post(`${API}/admin/newsletters`, { subject, content_md: contentMd, audience_slug: audienceSlug }, { withCredentials: true })).data.id;
      if (!draftId) setDraftId(id);
      const res = await axios.post(`${API}/admin/newsletters/${id}/preview`, {}, { withCredentials: true });
      if (res.data.success) toast.success(`Preview sent to ${res.data.preview_sent_to}`);
      else toast.info(res.data.message || "Preview skipped");
    } catch { toast.error("Failed to send preview."); }
    setPreviewing(false);
  }

  async function openVisualPreview() {
    if (!subject.trim() && !contentMd.trim()) { toast.error("Add a subject or content first."); return; }
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/admin/newsletters/preview-render`,
        { subject: subject || "Preview", content_md: contentMd || "<p>Start writing your newsletter…</p>" },
        { withCredentials: true }
      );
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch { toast.error("Could not render preview."); }
    setLoadingPreview(false);
  }

  async function doSend() {
    setConfirmSend(false);
    setSending(true);
    let idToSend = draftId;
    if (!idToSend) {
      try {
        const res = await axios.post(`${API}/admin/newsletters`, { subject, content_md: contentMd, audience_slug: audienceSlug }, { withCredentials: true });
        idToSend = res.data.id;
        setDraftId(idToSend);
      } catch { toast.error("Failed to save before sending."); setSending(false); return; }
    }
    try {
      const res = await axios.post(`${API}/admin/newsletters/${idToSend}/send`, {}, { withCredentials: true });
      setLastResult(res.data);
      toast.success(`Sent to ${res.data.recipient_count} subscribers.`);
      resetComposer();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    }
    setSending(false);
  }

  async function deleteDraft(id) {
    try {
      await axios.delete(`${API}/admin/newsletters/${id}`, { withCredentials: true });
      toast.success("Draft deleted.");
      fetchAll();
    } catch { toast.error("Could not delete."); }
  }

  async function createAudience() {
    if (!newAud.name || !newAud.slug) { toast.error("Name and slug are required."); return; }
    try {
      await axios.post(`${API}/admin/audiences`, newAud, { withCredentials: true });
      toast.success(`Audience "${newAud.name}" created.`);
      setNewAud({ name: "", slug: "", description: "" });
      setShowNewAudience(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-navy/30" /></div>;

  const sentNewsletters = newsletters.filter(n => n.status === "sent");
  const drafts = newsletters.filter(n => n.status === "draft");

  return (
    <div data-testid="newsletter-tab">
      {/* Audiences */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-lora text-navy text-lg font-normal">Audience Segments</h3>
            <p className="text-navy/40 font-serif text-xs mt-0.5">Newsletter recipients are grouped by segment. Add new segments without code changes.</p>
          </div>
          <button
            onClick={() => setShowNewAudience(!showNewAudience)}
            className="flex items-center gap-2 text-xs border border-soft-gray px-4 py-2 font-lora tracking-widest uppercase text-navy/60 hover:bg-white transition-colors"
            data-testid="create-audience-btn"
          >
            <Plus size={12} /> New Segment
          </button>
        </div>

        {showNewAudience && (
          <div className="bg-white border border-gold/40 p-5 mb-4" data-testid="new-audience-form">
            <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mb-4">New Audience Segment</p>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block font-lora text-[10px] tracking-widest uppercase text-navy/40 mb-1.5">Display Name</label>
                <input value={newAud.name} onChange={e => setNewAud(p => ({...p, name: e.target.value}))} placeholder="e.g. Blog Subscribers" className="w-full border border-soft-gray px-3 py-2 font-serif text-sm text-navy focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="block font-lora text-[10px] tracking-widest uppercase text-navy/40 mb-1.5">Slug (unique)</label>
                <input value={newAud.slug} onChange={e => setNewAud(p => ({...p, slug: e.target.value.toLowerCase().replace(/\s+/g,'-')}))} placeholder="e.g. blog-subscribers" className="w-full border border-soft-gray px-3 py-2 font-serif text-sm text-navy focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="block font-lora text-[10px] tracking-widest uppercase text-navy/40 mb-1.5">Description (optional)</label>
                <input value={newAud.description} onChange={e => setNewAud(p => ({...p, description: e.target.value}))} placeholder="How subscribers join this segment" className="w-full border border-soft-gray px-3 py-2 font-serif text-sm text-navy focus:outline-none focus:border-gold" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={createAudience} className="bg-navy text-ivory px-5 py-2 font-lora text-xs tracking-widest uppercase hover:bg-navy/90 transition-colors">Create Segment</button>
              <button onClick={() => setShowNewAudience(false)} className="text-navy/40 font-serif text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {audiences.map(aud => (
            <div key={aud.slug} className={`bg-white border p-5 cursor-pointer transition-all ${audienceSlug === aud.slug ? "border-gold shadow-sm" : "border-soft-gray hover:border-gold/40"}`}
              onClick={() => setAudienceSlug(aud.slug)} data-testid={`audience-card-${aud.slug}`}>
              <div className="flex items-start justify-between mb-3">
                <p className="font-lora text-sm text-navy">{aud.name}</p>
                {audienceSlug === aud.slug && <div className="w-2 h-2 rounded-full bg-gold mt-1" />}
              </div>
              <p className="font-lora text-2xl text-navy mb-1">{aud.active_subscribers}</p>
              <p className="font-lora text-[10px] tracking-widest uppercase text-navy/40">Active Subscribers</p>
              {aud.unsubscribed > 0 && <p className="font-serif text-xs text-navy/30 mt-1">{aud.unsubscribed} unsubscribed</p>}
              {aud.description && <p className="font-serif text-xs text-navy/40 mt-3 italic border-t border-soft-gray pt-2">{aud.description}</p>}
            </div>
          ))}
          {/* Future segments placeholder */}
          <div className="bg-[#FAFAF8] border border-dashed border-soft-gray p-5 flex flex-col items-center justify-center text-center" data-testid="future-segments-hint">
            <Plus size={16} className="text-navy/20 mb-2" />
            <p className="font-lora text-xs tracking-widest uppercase text-navy/30">Future Segments</p>
            <p className="font-serif text-xs text-navy/25 mt-1">Blog, events, clients</p>
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="bg-white border border-soft-gray p-6 mb-6" data-testid="newsletter-composer">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-lora text-navy text-lg font-normal">{editingDraft ? "Edit Draft" : "Compose Newsletter"}</h3>
            {activeAudience && (
              <p className="text-navy/40 font-serif text-xs mt-0.5">
                Sending to <strong className="text-navy/60">{activeAudience.active_subscribers}</strong> active subscribers in <em>{activeAudience.name}</em>
              </p>
            )}
          </div>
          {editingDraft && (
            <button onClick={resetComposer} className="text-navy/30 text-xs font-serif hover:text-navy/60 transition-colors flex items-center gap-1">
              <X size={12} /> New newsletter
            </button>
          )}
        </div>

        <div className="mb-4">
          <label className="block font-lora text-[10px] tracking-widest uppercase text-navy/40 mb-1.5">Subject Line</label>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="e.g. Navigating the Career Crossroads"
            className="w-full border border-soft-gray px-4 py-3 font-serif text-sm text-navy focus:outline-none focus:border-gold"
            data-testid="newsletter-subject-input"
          />
        </div>

        <div className="mb-4">
          <label className="block font-lora text-[10px] tracking-widest uppercase text-navy/40 mb-1.5">Content</label>
          <div data-testid="newsletter-content-editor">
            <RichTextEditor
              value={contentMd}
              onChange={val => setContentMd(val || "")}
            />
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={saveDraft}
            disabled={saving}
            className="border border-soft-gray px-5 py-2.5 font-lora text-xs tracking-widest uppercase text-navy/60 hover:bg-[#F9F9F6] transition-colors disabled:opacity-40"
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin inline" /> : "Save Draft"}
          </button>
          <button
            onClick={openVisualPreview}
            disabled={loadingPreview}
            className="flex items-center gap-1.5 border border-navy/20 px-5 py-2.5 font-lora text-xs tracking-widest uppercase text-navy/60 hover:bg-[#F9F9F6] hover:border-navy/40 transition-colors disabled:opacity-40"
            data-testid="visual-preview-btn"
          >
            {loadingPreview ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
            Preview Email
          </button>
          <button
            onClick={sendPreview}
            disabled={previewing || !subject.trim()}
            className="border border-gold/60 px-5 py-2.5 font-lora text-xs tracking-widest uppercase text-gold hover:bg-gold/5 transition-colors disabled:opacity-40"
            data-testid="send-preview-btn"
          >
            {previewing ? <Loader2 size={12} className="animate-spin inline" /> : "Send Preview Email"}
          </button>
          <button
            onClick={() => { if (!subject.trim() || !contentMd.trim()) { toast.error("Subject and content required."); return; } setConfirmSend(true); }}
            disabled={sending || !subject.trim() || !contentMd.trim()}
            className="bg-navy text-ivory px-6 py-2.5 font-lora text-xs tracking-widest uppercase hover:bg-navy/90 transition-colors disabled:opacity-40"
            data-testid="send-newsletter-btn"
          >
            {sending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
            Send to {activeAudience?.active_subscribers ?? "—"} Subscribers
          </button>
        </div>

        {lastResult && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 flex items-center gap-3" data-testid="send-success-banner">
            <Check size={16} className="text-green-600 flex-shrink-0" />
            <p className="font-serif text-sm text-green-800">
              Newsletter sent to <strong>{lastResult.recipient_count}</strong> subscribers in <em>{lastResult.audience}</em>.
            </p>
          </div>
        )}
      </div>

      {/* Confirm send modal */}
      {confirmSend && (
        <div className="fixed inset-0 bg-navy/60 flex items-center justify-center z-50" data-testid="confirm-send-modal">
          <div className="bg-white p-8 max-w-sm w-full mx-4">
            <p className="font-lora text-navy text-lg mb-2 font-normal">Send Newsletter?</p>
            <p className="font-serif text-navy/60 text-sm leading-relaxed mb-6">
              This will send <strong>"{subject}"</strong> to{" "}
              <strong>{activeAudience?.active_subscribers ?? "—"} active subscribers</strong> in{" "}
              <em>{activeAudience?.name}</em>. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={doSend} className="bg-navy text-ivory px-6 py-2.5 font-lora text-xs tracking-widest uppercase hover:bg-navy/90 transition-colors" data-testid="confirm-send-yes">
                Yes, Send
              </button>
              <button onClick={() => setConfirmSend(false)} className="border border-soft-gray px-5 py-2.5 font-lora text-xs tracking-widest uppercase text-navy/60 hover:bg-[#F9F9F6] transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Email Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-navy/80 z-[60] flex flex-col"
          data-testid="visual-preview-modal"
          onClick={e => { if (e.target === e.currentTarget) setShowPreview(false); }}
        >
          {/* Toolbar */}
          <div className="flex items-center justify-between bg-[#0A1628] px-6 py-3 shrink-0">
            <div className="flex items-center gap-3">
              <Eye size={14} className="text-gold/80" />
              <p className="font-lora text-[11px] tracking-widest uppercase text-ivory/70">Email Preview</p>
              {subject && <span className="font-serif text-xs text-ivory/40 truncate max-w-xs">— {subject}</span>}
            </div>
            <button
              onClick={() => setShowPreview(false)}
              className="text-ivory/50 hover:text-ivory transition-colors flex items-center gap-1.5 font-lora text-xs tracking-widest uppercase"
              data-testid="close-visual-preview"
            >
              <X size={14} /> Close Preview
            </button>
          </div>
          {/* Email frame */}
          <div className="flex-1 overflow-y-auto bg-[#E8E4DF] flex justify-center py-6 px-4">
            <div className="w-full max-w-[640px] shadow-2xl">
              <iframe
                srcDoc={previewHtml}
                title="Newsletter Preview"
                className="w-full border-0 bg-white"
                style={{ height: "900px", display: "block" }}
                sandbox="allow-same-origin"
                data-testid="preview-iframe"
              />
            </div>
          </div>
        </div>
      )}

      {/* Drafts */}
      {drafts.length > 0 && (
        <div className="bg-white border border-soft-gray p-6 mb-6" data-testid="drafts-section">
          <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mb-4">Saved Drafts</p>
          <div className="space-y-2">
            {drafts.map(nl => (
              <div key={nl.id} className="flex items-center justify-between py-3 border-b border-soft-gray last:border-0">
                <div>
                  <p className="font-serif text-sm text-navy">{nl.subject}</p>
                  <p className="font-serif text-xs text-navy/40 mt-0.5">{nl.created_at?.substring(0,10)} · {nl.audience_slug}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => loadDraft(nl)} className="text-xs border border-soft-gray px-3 py-1.5 text-navy/60 hover:bg-[#F9F9F6]">Edit</button>
                  <button onClick={() => deleteDraft(nl.id)} className="text-xs text-red-400 hover:text-red-600">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-soft-gray p-6" data-testid="newsletter-history">
        <p className="font-lora text-xs tracking-widest uppercase text-navy/40 mb-5">Sent Newsletters</p>
        {sentNewsletters.length === 0 ? (
          <p className="font-serif text-navy/30 text-sm text-center py-6">No newsletters sent yet. Compose and send your first above.</p>
        ) : (
          <>
            <OpenRateChart newsletters={sentNewsletters} />
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-soft-gray">
                  {["Subject", "Audience", "Sent", "Recipients", "Opens", "Open Rate"].map(h => (
                    <th key={h} className="text-left px-3 py-3 font-lora text-[10px] tracking-widest uppercase text-navy/40">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sentNewsletters.map(nl => {
                  const openRate = nl.recipient_count ? Math.round((nl.open_count || 0) / nl.recipient_count * 100) : 0;
                  return (
                    <tr key={nl.id} className="border-b border-soft-gray hover:bg-[#F9F9F6]" data-testid={`newsletter-row-${nl.id}`}>
                      <td className="px-3 py-3 font-serif text-navy max-w-[200px] truncate">{nl.subject}</td>
                      <td className="px-3 py-3 text-navy/50 text-xs">{nl.audience_slug}</td>
                      <td className="px-3 py-3 text-navy/40 text-xs">{nl.sent_at?.substring(0,10)}</td>
                      <td className="px-3 py-3 font-lora text-navy">{nl.recipient_count ?? 0}</td>
                      <td className="px-3 py-3 font-lora text-navy">{nl.open_count ?? 0}</td>
                      <td className="px-3 py-3">
                        <span className={`text-xs px-2 py-1 ${openRate >= 30 ? "bg-green-50 text-green-700 border border-green-200" : "bg-[#F9F9F6] text-navy/50 border border-soft-gray"}`}>
                          {openRate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ─── Main AdminPage ───────────────────────────────────────────────────────────────

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMaterial, setNewMaterial] = useState({ title: "", description: "", url: "", user_email: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [refundModal, setRefundModal] = useState(null); // booking object or null
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [refundMsg, setRefundMsg] = useState("");
  const [deleteBookingTarget, setDeleteBookingTarget] = useState(null);
  const [deletingBooking, setDeletingBooking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, uRes, mRes, cRes, lRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/materials`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
        axios.get(`${API}/admin/leads`, { withCredentials: true }),
      ]);
      setBookings(bRes.data);
      setUsers(uRes.data);
      setMaterials(mRes.data);
      setContacts(cRes.data);
      setLeads(lRes.data);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.warn("[Admin] Save failed:", err?.message || err); }
    finally { setLoading(false); }
  };

  // Intentional: empty dep array runs this only on mount; `load` is a stable local fn and including it in deps would cause re-fetch loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}`, { status }, { withCredentials: true });
      setBookings(prev => prev.map(b => b._id === id ? { ...b, status } : b));
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.warn("[Admin] Save failed:", err?.message || err); }
  };

  const openRefundModal = (booking) => {
    setRefundModal(booking);
    setRefundAmount(String(booking.total || ""));
    setRefundReason("");
    setRefundMsg("");
  };

  const submitRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (!amount || amount <= 0 || amount > parseFloat(refundModal.total)) {
      setRefundMsg(`Enter an amount between $0.01 and $${refundModal.total}`);
      return;
    }
    setRefunding(true);
    setRefundMsg("");
    try {
      await axios.post(
        `${API}/admin/bookings/${refundModal._id}/refund`,
        { amount, reason: refundReason },
        { withCredentials: true }
      );
      setBookings(prev => prev.map(b =>
        b._id === refundModal._id
          ? { ...b, status: "cancelled", payment_status: "refunded", refunded_amount: amount }
          : b
      ));
      setRefundModal(null);
    } catch (err) {
      setRefundMsg(err?.response?.data?.detail || "Refund failed — please try again.");
    } finally {
      setRefunding(false);
    }
  };

  const handleDeleteBooking = async () => {
    if (!deleteBookingTarget) return;
    setDeletingBooking(true);
    try {
      await axios.delete(`${API}/admin/bookings/${deleteBookingTarget._id}`, { withCredentials: true });
      setBookings(prev => prev.filter(b => b._id !== deleteBookingTarget._id));
      toast.success("Booking permanently deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed — please try again.");
    } finally {
      setDeletingBooking(false);
      setDeleteBookingTarget(null);
    }
  };

  const addMaterial = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/materials`, newMaterial, { withCredentials: true });
      setMaterials(prev => [res.data, ...prev]);
      setNewMaterial({ title: "", description: "", url: "", user_email: "" });
      setMsg("Material added successfully.");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("Failed to add material."); }
    setSaving(false);
  };

  const deleteMaterial = async id => {
    try {
      await axios.delete(`${API}/admin/materials/${id}`, { withCredentials: true });
      setMaterials(prev => prev.filter(m => m._id !== id));
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.warn("[Admin] Save failed:", err?.message || err); }
  };

  const STATUS_BADGE = {
    confirmed: "bg-green-50 text-green-700",
    pending_payment: "bg-yellow-50 text-yellow-700",
    cancelled: "bg-red-50 text-red-600",
    completed: "bg-blue-50 text-blue-700",
  };

  if (loading) return (
    <div className="min-h-screen bg-ivory flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-navy/40" />
    </div>
  );

  const TABS = [
    { id: "bookings", label: "Bookings", icon: Calendar },
    { id: "users", label: "Clients", icon: Users },
    { id: "materials", label: "Materials", icon: Package },
    { id: "contacts", label: "Messages", icon: MessageSquare },
    { id: "leads", label: "Leads", icon: Mail },
    { id: "analytics", label: "Analytics", icon: BarChart2 },
    { id: "newsletter", label: "Newsletter", icon: Send },
    { id: "blog", label: "Blog", icon: Edit3 },
    { id: "availability", label: "Availability", icon: Clock },
    { id: "content", label: "Site Content", icon: Edit3 },
  ];

  return (
    <>
    <div className="min-h-screen bg-ivory" data-testid="admin-page">
      {/* Header */}
      <div className="bg-navy px-6 lg:px-12 py-12">
        <div className="max-w-7xl mx-auto">
          <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-1">Administration</p>
          <h1 className="font-lora text-3xl text-ivory font-normal">Management Console</h1>
          <p className="text-ivory/40 font-serif text-sm mt-1">Logged in as {user?.email}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border-b border-soft-gray">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 grid grid-cols-5 divide-x divide-soft-gray">
          {[
            { label: "Total Bookings", val: bookings.length },
            { label: "Confirmed", val: bookings.filter(b => b.status === "confirmed").length },
            { label: "Clients", val: users.filter(u => u.role !== "admin").length },
            { label: "Leads", val: leads.length },
            { label: "Opened", val: leads.filter(l => l.email_opened).length },
          ].map(stat => (
            <div key={stat.label} className="px-6 text-center first:pl-0 last:pr-0">
              <p className="font-lora text-2xl text-navy">{stat.val}</p>
              <p className="text-navy/40 text-xs tracking-widest uppercase mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white border border-soft-gray p-1 w-fit overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs tracking-widest uppercase whitespace-nowrap transition-all ${
                tab === t.id ? "bg-navy text-ivory" : "text-navy/50 hover:text-navy"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {/* Bookings */}
        {tab === "bookings" && (
          <div className="overflow-x-auto" data-testid="admin-bookings-table">
            <table className="w-full bg-white border border-soft-gray text-sm">
              <thead>
                <tr className="border-b border-soft-gray">
                  {["Client", "Service", "Date & Time", "Total", "Status", "Actions"].map(h => (
                    <th key={h} className="text-left px-5 py-4 font-lora text-xs tracking-widest uppercase text-navy/40">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b._id} className="border-b border-soft-gray hover:bg-[#F9F9F6] transition-colors">
                    <td className="px-5 py-4"><p className="font-serif text-navy">{b.user_name}</p><p className="text-navy/40 text-xs">{b.user_email}</p></td>
                    <td className="px-5 py-4 font-serif text-navy/70 max-w-[180px]">{b.service_name}</td>
                    <td className="px-5 py-4 font-serif text-navy/70">{b.date}<br /><span className="text-xs text-navy/40">{b.time_slot}</span></td>
                    <td className="px-5 py-4 font-lora text-navy">${b.total}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2 py-1 rounded-sm ${STATUS_BADGE[b.status] || "bg-gray-50 text-gray-600"}`}>{b.status?.replace("_", " ")}</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <select value={b.status} onChange={e => updateBookingStatus(b._id, e.target.value)} className="border border-soft-gray bg-white px-2 py-1 text-xs text-navy focus:outline-none focus:border-navy" data-testid={`booking-status-select-${b._id}`}>
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                        </select>
                        {b.payment_status === "paid" && b.status !== "cancelled" && (
                          <button
                            onClick={() => openRefundModal(b)}
                            className="text-xs px-3 py-1 border border-red-300 text-red-600 hover:bg-red-50 transition-colors font-serif whitespace-nowrap"
                            data-testid={`refund-btn-${b._id}`}
                          >
                            Refund &amp; Cancel
                          </button>
                        )}
                        {b.payment_status === "refunded" && (
                          <span className="text-xs text-red-400 font-serif italic whitespace-nowrap">
                            Refunded ${b.refunded_amount}
                          </span>
                        )}
                        <button
                          onClick={() => setDeleteBookingTarget(b)}
                          className="text-xs px-3 py-1 border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-400 hover:text-red-700 transition-colors font-serif whitespace-nowrap flex items-center gap-1"
                          data-testid={`delete-booking-btn-${b._id}`}
                          title="Permanently delete this booking"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bookings.length === 0 && <p className="text-center text-navy/40 font-serif py-10">No bookings yet.</p>}
          </div>
        )}

        {/* Users */}
        {tab === "users" && (
          <div className="overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full bg-white border border-soft-gray text-sm">
              <thead>
                <tr className="border-b border-soft-gray">
                  {["Name", "Email", "Role", "Joined"].map(h => (
                    <th key={h} className="text-left px-5 py-4 font-lora text-xs tracking-widest uppercase text-navy/40">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} className="border-b border-soft-gray hover:bg-[#F9F9F6]">
                    <td className="px-5 py-4 font-serif text-navy">{u.name}</td>
                    <td className="px-5 py-4 font-serif text-navy/70">{u.email}</td>
                    <td className="px-5 py-4"><span className={`text-xs px-2 py-1 rounded-sm ${u.role === "admin" ? "bg-navy text-ivory" : "bg-soft-gray text-navy/70"}`}>{u.role}</span></td>
                    <td className="px-5 py-4 text-navy/40 text-xs">{u.created_at?.substring(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Materials */}
        {tab === "materials" && (
          <div data-testid="admin-materials">
            <div className="bg-white border border-soft-gray p-6 mb-6">
              <h3 className="font-lora text-navy text-lg font-normal mb-4">Add Material</h3>
              <form onSubmit={addMaterial} className="grid sm:grid-cols-2 gap-4">
                <input required value={newMaterial.title} onChange={e => setNewMaterial(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="border border-soft-gray px-3 py-2.5 text-sm text-navy font-serif focus:outline-none focus:border-navy" data-testid="material-title-input" />
                <input value={newMaterial.user_email} onChange={e => setNewMaterial(p => ({ ...p, user_email: e.target.value }))} placeholder="Client Email (optional)" className="border border-soft-gray px-3 py-2.5 text-sm text-navy font-serif focus:outline-none focus:border-navy" data-testid="material-email-input" />
                <input value={newMaterial.url} onChange={e => setNewMaterial(p => ({ ...p, url: e.target.value }))} placeholder="Resource URL (optional)" className="border border-soft-gray px-3 py-2.5 text-sm text-navy font-serif focus:outline-none focus:border-navy col-span-full" data-testid="material-url-input" />
                <textarea value={newMaterial.description} onChange={e => setNewMaterial(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" rows={2} className="border border-soft-gray px-3 py-2.5 text-sm text-navy font-serif focus:outline-none focus:border-navy resize-none col-span-full" data-testid="material-desc-input" />
                <button type="submit" disabled={saving} className="bg-navy text-ivory px-6 py-2.5 text-xs tracking-widest uppercase hover:bg-navy/90 transition-all disabled:opacity-60 flex items-center gap-2" data-testid="add-material-btn">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Add Material
                </button>
              </form>
              {msg && <p className="mt-3 text-sm font-serif text-navy/60">{msg}</p>}
            </div>
            <div className="space-y-3">
              {materials.map(m => (
                <div key={m._id} className="bg-white border border-soft-gray p-5 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-lora text-navy text-base">{m.title}</p>
                    <p className="text-navy/50 font-serif text-sm">{m.description}</p>
                    {m.user_email && <p className="text-navy/30 text-xs mt-1">Client: {m.user_email}</p>}
                    {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="text-gold text-xs mt-1 inline-block hover:opacity-70">View &rarr;</a>}
                  </div>
                  <button onClick={() => deleteMaterial(m._id)} className="text-red-400 hover:text-red-600 text-xs tracking-widest uppercase shrink-0 transition-colors" data-testid={`delete-material-${m._id}`}>Remove</button>
                </div>
              ))}
              {materials.length === 0 && <p className="text-center text-navy/40 font-serif py-10">No materials yet.</p>}
            </div>
          </div>
        )}

        {/* Contacts */}
        {tab === "contacts" && (
          <div className="space-y-4" data-testid="admin-contacts">
            {contacts.map(c => (
              <div key={c._id} className="bg-white border border-soft-gray p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-lora text-navy text-base">{c.name}</p>
                    <p className="text-navy/50 font-serif text-sm">{c.email}</p>
                  </div>
                  <p className="text-navy/30 text-xs">{c.created_at?.substring(0, 10)}</p>
                </div>
                <p className="text-navy/70 font-serif text-sm leading-relaxed">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-center text-navy/40 font-serif py-10">No messages yet.</p>}
          </div>
        )}

        {/* Leads */}
        {tab === "leads" && (
          <div data-testid="admin-leads">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-lora text-navy text-lg font-normal">Lead Magnet Captures</h3>
                <p className="text-navy/40 font-serif text-xs mt-0.5">
                  {leads.length} lead{leads.length !== 1 ? "s" : ""} captured &nbsp;·&nbsp; {leads.filter(l => l.email_opened).length} opened their email
                </p>
              </div>
              <a
                href={`${API}/admin/leads/export`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 border border-soft-gray px-4 py-2 font-lora text-xs tracking-widest uppercase text-navy/60 hover:bg-white transition-colors"
                data-testid="export-leads-btn"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export CSV
              </a>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full bg-white border border-soft-gray text-sm">
                <thead>
                  <tr className="border-b border-soft-gray">
                    {["Name", "Email", "Captured", "Email Opened", "CTA", "Nurtured", "Status"].map(h => (
                      <th key={h} className="text-left px-4 py-4 font-lora text-xs tracking-widest uppercase text-navy/40">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map(l => {
                    const nurtureSent = (l.nurture_emails || []).filter(e => e.sent).length;
                    return (
                      <tr key={l._id} className="border-b border-soft-gray hover:bg-[#F9F9F6] transition-colors" data-testid={`lead-row-${l._id}`}>
                        <td className="px-4 py-4 font-serif text-navy">{l.name}</td>
                        <td className="px-4 py-4 font-serif text-navy/70">{l.email}</td>
                        <td className="px-4 py-4 text-navy/40 text-xs">{l.created_at?.substring(0, 10)}</td>
                        <td className="px-4 py-4">
                          {l.email_opened ? (
                            <span className="text-xs px-2 py-1 bg-green-50 text-green-700 border border-green-200">
                              {l.open_count > 1 ? `Opened ×${l.open_count}` : "Opened"}
                            </span>
                          ) : (
                            <span className="text-xs text-navy/30">--</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {l.cta_clicked ? (
                            <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200">Clicked</span>
                          ) : (
                            <span className="text-xs text-navy/30">--</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-navy/50 text-xs">{nurtureSent}/3</td>
                        <td className="px-4 py-4">
                          {l.unsubscribed ? (
                            <span className="text-xs px-2 py-1 bg-red-50 text-red-600 border border-red-200">Unsub</span>
                          ) : (
                            <span className="text-xs px-2 py-1 bg-[#F9F9F6] text-navy/40 border border-soft-gray">Active</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {leads.length === 0 && <p className="text-center text-navy/40 font-serif py-10">No leads captured yet. The lead magnet form on the homepage will populate this list.</p>}
            </div>
          </div>
        )}

        {/* Blog CMS */}
        {tab === "blog" && <BlogCMS />}

        {/* Newsletter */}
        {tab === "newsletter" && <NewsletterTab />}

        {/* Analytics */}
        {tab === "analytics" && <AnalyticsDashboard />}

        {/* Availability Editor */}
        {tab === "availability" && <AvailabilityEditor />}

        {/* Content Editor */}
        {tab === "content" && <ContentEditor />}
      </div>
    </div>

    {/* Delete Booking Confirmation Dialog */}
    <AlertDialog open={!!deleteBookingTarget} onOpenChange={open => { if (!open) setDeleteBookingTarget(null); }}>
      <AlertDialogContent className="bg-ivory border border-soft-gray rounded-none max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-lora text-navy text-xl font-normal">Permanently Delete Booking?</AlertDialogTitle>
          <AlertDialogDescription className="font-serif text-navy/60 text-sm leading-relaxed">
            You are about to permanently delete the booking for{" "}
            <span className="text-navy font-semibold">{deleteBookingTarget?.user_name}</span>
            {" "}({deleteBookingTarget?.service_name}, {deleteBookingTarget?.date} at {deleteBookingTarget?.time_slot}).
            <br /><br />
            This will remove the record from the database, free the time slot, and unlink any related records. This cannot be undone. No emails or Zoom links will be triggered.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-3">
          <AlertDialogCancel disabled={deletingBooking} className="font-serif text-sm bg-transparent border border-soft-gray text-navy hover:bg-soft-gray/30 rounded-none" data-testid="delete-booking-cancel-btn">
            Keep Booking
          </AlertDialogCancel>
          <AlertDialogAction disabled={deletingBooking} onClick={handleDeleteBooking}
            className="font-serif text-sm bg-red-700 text-white hover:bg-red-800 border-0 rounded-none"
            data-testid="delete-booking-confirm-btn">
            {deletingBooking ? "Deleting…" : "Delete Permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Refund & Cancel Modal */}
    {refundModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 backdrop-blur-sm" data-testid="refund-modal">
        <div className="bg-white border border-soft-gray w-full max-w-md mx-4 shadow-2xl">
          {/* Header */}
          <div className="bg-navy px-7 py-5 flex items-center justify-between">
            <div>
              <p className="font-lora text-[10px] tracking-[0.25em] uppercase text-[#C2B8A3]">Admin Action</p>
              <h2 className="font-lora text-white text-lg mt-0.5">Refund &amp; Cancel</h2>
            </div>
            <button onClick={() => setRefundModal(null)} className="text-white/40 hover:text-white transition-colors" data-testid="close-refund-modal">
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-7 py-6 space-y-5">
            {/* Booking summary */}
            <div className="bg-[#F9F9F6] border border-soft-gray p-4 space-y-1.5">
              <p className="font-serif text-navy text-sm font-semibold">{refundModal.user_name}</p>
              <p className="font-serif text-navy/60 text-xs">{refundModal.service_name}</p>
              <p className="font-serif text-navy/50 text-xs">{refundModal.date} &middot; {refundModal.time_slot}</p>
              <p className="font-lora text-navy text-xs mt-1 tracking-wide">Charged: <span className="font-semibold">${refundModal.total} CAD</span></p>
            </div>

            {/* Amount */}
            <div>
              <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">
                Refund Amount (CAD) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40 text-sm font-serif">$</span>
                <input
                  type="number"
                  min="0.01"
                  max={refundModal.total}
                  step="0.01"
                  value={refundAmount}
                  onChange={e => setRefundAmount(e.target.value)}
                  className="w-full border border-soft-gray bg-white pl-7 pr-3 py-2.5 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                  placeholder={String(refundModal.total)}
                  data-testid="refund-amount-input"
                />
              </div>
              <p className="text-navy/30 text-xs mt-1 font-serif">Max: ${refundModal.total} CAD</p>
            </div>

            {/* Reason */}
            <div>
              <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-navy/40 mb-1.5">
                Reason (optional — shown in email)
              </label>
              <textarea
                rows={2}
                value={refundReason}
                onChange={e => setRefundReason(e.target.value)}
                className="w-full border border-soft-gray bg-white px-3 py-2 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors resize-none"
                placeholder="e.g. Client request, schedule conflict..."
                data-testid="refund-reason-input"
              />
            </div>

            {refundMsg && (
              <p className="text-red-600 text-sm font-serif" data-testid="refund-error-msg">{refundMsg}</p>
            )}

            <div className="bg-amber-50 border border-amber-200 px-4 py-3">
              <p className="text-amber-800 text-xs font-serif leading-relaxed">
                This will issue a Stripe refund, mark the booking as cancelled, and send a cancellation email to <strong>{refundModal.user_email}</strong>. This action cannot be undone.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-7 py-5 border-t border-soft-gray flex items-center justify-end gap-3">
            <button
              onClick={() => setRefundModal(null)}
              className="font-serif text-sm text-navy/40 hover:text-navy underline underline-offset-2 transition-colors"
              data-testid="cancel-refund-modal"
            >
              Cancel
            </button>
            <button
              onClick={submitRefund}
              disabled={refunding}
              className="bg-red-600 text-white px-6 py-2.5 font-lora text-xs tracking-widest uppercase hover:bg-red-700 transition-colors disabled:opacity-50"
              data-testid="confirm-refund-btn"
            >
              {refunding ? "Processing..." : "Confirm Refund"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
